import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {FlatField} from '../model/flat-field';

/**
 * Service de gestion des champs duplicables dans les formulaires eForm.
 *
 * Ce service gère l'ajout, la suppression et la visibilité des éléments duplicables
 * (LOT, ORG, CON, TEN, RES, TPA, etc.)
 *
 * @version 1.0
 */
@Injectable({
  providedIn: 'root'
})
export class EformDuplicableFieldsService {
  /** BehaviorSubject pour indiquer si un élément duplicable est en cours de rechargement */
  private isReloadingDuplicableElement = new BehaviorSubject<boolean>(false);

  /** BehaviorSubject pour indiquer si une préférence est en cours de chargement */
  private isLoadingPreference = new BehaviorSubject<boolean>(false);

  /** BehaviorSubject pour indiquer si un avis lié est en cours de chargement */
  private isLoadingAvisLie = new BehaviorSubject<boolean>(false);

  /**
   * ✅ FIX 2026-03-05: BehaviorSubject UNIQUEMENT pour les LOT
   * Protège les champs conditionnels (comme BT-5421-Lot) pendant le chargement
   * d'un LOT existant sans impacter ORG/TEN/RES/CON/TPA
   */
  private isLoadingLot = new BehaviorSubject<boolean>(false);

  /**
   * ✅ FIX 2026-03-05: Map pour tracker les éléments duplicables en cours de chargement initial
   * Clé: type d'élément (LOT, CON, ORG, etc.)
   * Valeur: nombre d'éléments de ce type en cours de chargement
   * Cela protège les champs conditionnels (comme BT-3202-Contract, BT-5421-Lot)
   * pendant le chargement sans impacter les autres types
   */
  private loadingDuplicableElements = new Map<string, number>();

  /** Map pour tracker le nombre de rechargements en cours PAR TYPE */
  private reloadingByType = new Map<string, number>();

  /** Subject privé pour notifier quand tous les rechargements sont terminés */
  private allReloadingCompleteSubject = new Subject<void>();

  /** Subject pour notifier quand le rechargement d'un TYPE spécifique est terminé */
  private reloadingCompleteForTypeSubject = new Subject<string>();

  /** Flag pour indiquer que le nettoyage post-rechargement est en cours */
  private isCleaningUpAfterReload = new BehaviorSubject<boolean>(false);

  /** Référence à la fonction de récupération des flatFields (injectée) */
  private getFlatFieldsValue: () => Map<string, FlatField[]> = () => new Map();
  private setFlatFields: (value: Map<string, FlatField[]>) => void = () => {
  };

  constructor() {
  }

  /**
   * Réinitialise tous les flags et compteurs du service.
   * À appeler lors de la destruction du composant pour éviter que les flags restent bloqués.
   */
  resetAllFlags(): void {
    this.isReloadingDuplicableElement.next(false);
    this.isLoadingPreference.next(false);
    this.isLoadingAvisLie.next(false);
    this.isLoadingLot.next(false);
    this.loadingDuplicableElements.clear();
    this.reloadingByType.clear();
    this.isCleaningUpAfterReload.next(false);
  }

  /**
   * Configure les fonctions d'accès aux flatFields
   */
  setFlatFieldsAccessors(
    getValue: () => Map<string, FlatField[]>,
    setValue: (value: Map<string, FlatField[]>) => void
  ): void {
    this.getFlatFieldsValue = getValue;
    this.setFlatFields = setValue;
  }

  /**
   * Duplique dynamiquement un groupe de champs marqués comme duplicables.
   */
  addDuplicableFieldsByType(duplicableType: string): void {
    const currentMap = this.getFlatFieldsValue();
    const updatedMap = new Map<string, FlatField[]>(currentMap);

    updatedMap.forEach((fields, key) => {
      const matchingFields = fields.filter(f => f.duplicableType === duplicableType);

      if (matchingFields.length > 0) {
        const baseField = matchingFields[0];
        const nextIndex = Math.max(...matchingFields.map(f => f.duplicableIndex ?? 0)) + 1;

        const duplicatedField: FlatField = {
          ...baseField,
          duplicableIndex: nextIndex,
          path: baseField.path.replace(/\.\d+\./, `.${nextIndex}.`),
          value: null,
          isDisplayed: true
        };

        const updatedFields = fields.map(f =>
          f.duplicableType === duplicableType
            ? {...f, isDisplayed: false}
            : f
        );

        updatedMap.set(key, [...updatedFields, duplicatedField]);
      }
    });

    this.setFlatFields(updatedMap);
  }

  /**
   * Supprime un champ duplicable spécifique en fonction de son type et de son index,
   * puis réindexe les champs restants du même type.
   */
  removeDuplicableFieldByTypeAndIndex(duplicableType: string, indexToRemove: number): void {
    const currentMap = this.getFlatFieldsValue();
    const updatedMap = new Map<string, FlatField[]>(currentMap);

    updatedMap.forEach((fields, key) => {
      const matchingFields = fields.filter(f => f.duplicableType === duplicableType);

      if (matchingFields.some(f => f.duplicableIndex === indexToRemove)) {
        const remaining = matchingFields
          .filter(f => f.duplicableIndex !== indexToRemove)
          .sort((a, b) => (a.duplicableIndex ?? 0) - (b.duplicableIndex ?? 0));

        const reindexed = remaining.map((f, i) => ({
          ...f,
          duplicableIndex: i,
          path: f.path.replace(/\.\d+\./, `.${i}.`),
          isDisplayed: (duplicableType === 'PAR' || duplicableType === 'LOT') ? i === 0 : false
        }));

        const otherFields = fields.filter(f => f.duplicableType !== duplicableType);

        updatedMap.set(key, [...otherFields, ...reindexed]);
      }
    });

    this.setFlatFields(updatedMap);
  }

  /**
   * Met à jour la visibilité des champs pour un type d'objet duplicable donné.
   */
  updateDuplicableFieldsVisibility(type: string, newIndex: number): void {
    const currentFields = this.getFlatFieldsValue();
    let hasChanges = false;
    const adjustedIndex = newIndex - 1;

    currentFields.forEach((fields) => {
      fields.forEach(field => {
        if (field.duplicableType === type) {
          const shouldBeDisplayed = field.duplicableIndex === adjustedIndex;
          if (field.isDisplayed !== shouldBeDisplayed) {
            field.isDisplayed = shouldBeDisplayed;
            hasChanges = true;
          }
        }
      });
    });

    if (hasChanges) {
      this.setFlatFields(new Map(currentFields));
    }
  }

  /**
   * Modifie l'état de rechargement d'un élément duplicable avec tracking par TYPE.
   */
  setReloadingDuplicableElement(isReloading: boolean, elementType: string = 'UNKNOWN'): void {
    if (isReloading) {
      const currentCount = this.reloadingByType.get(elementType) || 0;
      this.reloadingByType.set(elementType, currentCount + 1);

      if (!this.isReloadingDuplicableElement.value) {
        this.isReloadingDuplicableElement.next(true);
      }
    } else {
      const currentCount = this.reloadingByType.get(elementType) || 0;
      const newCount = Math.max(0, currentCount - 1);

      if (newCount === 0) {
        this.reloadingByType.delete(elementType);
        // ✅ FIX 2026-01-07: Émettre le type quand son rechargement est complètement terminé
        this.reloadingCompleteForTypeSubject.next(elementType);
      } else {
        this.reloadingByType.set(elementType, newCount);
      }

      if (this.reloadingByType.size === 0 && this.isReloadingDuplicableElement.value) {
        this.isReloadingDuplicableElement.next(false);
        this.allReloadingCompleteSubject.next();
      }
    }
  }

  /**
   * Observable qui émet lorsque l'état de rechargement d'un élément duplicable change.
   */
  get isReloadingDuplicableElement$(): Observable<boolean> {
    return this.isReloadingDuplicableElement.asObservable();
  }

  /**
   * Retourne la valeur actuelle de isReloadingDuplicableElement
   */
  get isReloadingDuplicableElementValue(): boolean {
    return this.isReloadingDuplicableElement.value;
  }

  /**
   * Observable public qui s'émet quand TOUS les rechargements d'éléments duplicables sont terminés
   */
  get allReloadingComplete(): Observable<void> {
    return this.allReloadingCompleteSubject.asObservable();
  }

  /**
   * Observable qui émet le TYPE d'élément quand son rechargement est terminé
   * Permet de nettoyer les IDs liés à ce type spécifique
   */
  get reloadingCompleteForType$(): Observable<string> {
    return this.reloadingCompleteForTypeSubject.asObservable();
  }

  /**
   * Indique si le nettoyage post-rechargement d'un élément duplicable est en cours
   */
  isCleaningUpAfterReloadInProgress(): boolean {
    return this.isCleaningUpAfterReload.getValue();
  }

  /**
   * Observable pour être notifié quand le nettoyage post-rechargement commence/termine
   */
  get isCleaningUpAfterReload$(): Observable<boolean> {
    return this.isCleaningUpAfterReload.asObservable();
  }

  /**
   * Déclenche le nettoyage post-rechargement
   */
  triggerCleanupAfterReload(): void {
    // Nettoyage post-rechargement
  }

  /**
   * Active ou désactive le mode de chargement des préférences.
   * Lorsque actif, les conditions de visibilité sont désactivées pour permettre
   * le remplissage des champs conditionnels.
   */
  setLoadingPreference(isLoading: boolean): void {
    this.isLoadingPreference.next(isLoading);
  }

  /**
   * Retourne la valeur actuelle de isLoadingPreference
   */
  get isLoadingPreferenceValue(): boolean {
    return this.isLoadingPreference.value;
  }

  /**
   * Observable qui émet lorsque l'état de chargement des préférences change
   */
  get isLoadingPreference$(): Observable<boolean> {
    return this.isLoadingPreference.asObservable();
  }

  /**
   * Active ou désactive le mode de chargement d'un avis lié.
   * Lorsque actif, les conditions de visibilité sont désactivées pour permettre
   * le remplissage des champs conditionnels lors de la création d'un avis lié.
   */
  setLoadingAvisLie(isLoading: boolean): void {
    this.isLoadingAvisLie.next(isLoading);
  }

  /**
   * Retourne la valeur actuelle de isLoadingAvisLie
   */
  get isLoadingAvisLieValue(): boolean {
    return this.isLoadingAvisLie.value;
  }

  /**
   * Observable qui émet lorsque l'état de chargement d'un avis lié change
   */
  get isLoadingAvisLie$(): Observable<boolean> {
    return this.isLoadingAvisLie.asObservable();
  }

  /**
   * ✅ FIX 2026-03-05: Active ou désactive le mode de chargement d'un LOT.
   * Lorsque actif, les conditions de visibilité sont désactivées UNIQUEMENT pour les LOT
   * pour permettre le remplissage des champs conditionnels (comme BT-5421-Lot).
   * N'impacte PAS ORG/TEN/RES/CON/TPA.
   */
  setLoadingLot(isLoading: boolean): void {
    this.isLoadingLot.next(isLoading);
  }

  /**
   * Retourne la valeur actuelle de isLoadingLot
   */
  get isLoadingLotValue(): boolean {
    return this.isLoadingLot.value;
  }

  /**
   * Observable qui émet lorsque l'état de chargement d'un LOT change
   */
  get isLoadingLot$(): Observable<boolean> {
    return this.isLoadingLot.asObservable();
  }

  /**
   * ✅ FIX 2026-03-05: Active le chargement d'un élément duplicable d'un type donné.
   * Incrémente le compteur pour ce type d'élément.
   * Protège les champs conditionnels pendant le chargement initial (ex: BT-3202-Contract, BT-5421-Lot)
   * @param type Type d'élément duplicable (LOT, CON, ORG, TEN, RES, TPA)
   */
  startLoadingDuplicableElement(type: string): void {
    const count = this.loadingDuplicableElements.get(type) || 0;
    this.loadingDuplicableElements.set(type, count + 1);
  }

  /**
   * ✅ FIX 2026-03-05: Désactive le chargement d'un élément duplicable d'un type donné.
   * Décrémente le compteur pour ce type d'élément.
   * @param type Type d'élément duplicable (LOT, CON, ORG, TEN, RES, TPA)
   */
  stopLoadingDuplicableElement(type: string): void {
    const count = this.loadingDuplicableElements.get(type) || 0;
    if (count > 1) {
      this.loadingDuplicableElements.set(type, count - 1);
    } else {
      this.loadingDuplicableElements.delete(type);
    }
  }

  /**
   * ✅ FIX 2026-03-05: Vérifie si un type d'élément duplicable est en cours de chargement
   * @param type Type d'élément duplicable (LOT, CON, ORG, TEN, RES, TPA)
   * @returns true si au moins un élément de ce type est en chargement
   */
  isLoadingDuplicableElementOfType(type: string): boolean {
    return (this.loadingDuplicableElements.get(type) || 0) > 0;
  }

  /**
   * ✅ FIX 2026-03-05: Désactive le chargement de TOUS les types d'éléments duplicables.
   * Utilisé après le chargement complet du formulaire pour réactiver toutes les conditions.
   */
  stopLoadingAllDuplicableElements(): void {
    this.loadingDuplicableElements.clear();
  }
}

